import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '@/api/axios';

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const AdminMonthlyReport = () => {
  const navigate = useNavigate();
  const now = new Date();
  const [projects, setProjects] = useState([]);
  const [scribeFilters, setScribeFilters] = useState({
    month: now.getMonth() + 1,
    year: now.getFullYear(),
  });
  const [scribeData, setScribeData] = useState([]);
  const [scribeSummary, setScribeSummary] = useState(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/masters/projects');
        setProjects(res.data);
      } catch (error) {
        toast.error('Failed to load projects');
      }
    })();
  }, []);

  const calculateTimeDifference = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMins = Math.floor((end - start) / 60000);
    return { hours: Math.floor(diffMins / 60), mins: diffMins % 60 };
  };

  const checkExceedsTenHours = (entry) => {
    const start = new Date(entry.start_time);
    const end = new Date(entry.end_time);
    const durationMins = (end - start) / 60000;
    const preChartMins =
      entry.pre_charting_Hours * 60 + entry.pre_charting_mins;
    return durationMins + preChartMins > 600;
  };

  const generateReport = async () => {
    try {
      const response = await axiosInstance.get('/scribe', {
        params: { _: Date.now() },
      });
      const allData = response.data;

      const filtered = allData
        .filter((entry) => {
          const startDate = new Date(entry.start_time);
          return (
            startDate.getMonth() + 1 === parseInt(scribeFilters.month) &&
            startDate.getFullYear() === parseInt(scribeFilters.year)
          );
        })
        .sort((a, b) => {
          if (a.project !== b.project) return a.project.localeCompare(b.project);
          return new Date(a.start_time) - new Date(b.start_time);
        });

      let totalDurationMins = 0;
      let totalPreChartMins = 0;
      const projectSubtotals = {};
      const projectsWithData = new Set();

      filtered.forEach((entry) => {
        const duration = calculateTimeDifference(entry.start_time, entry.end_time);
        const durationMins = duration.hours * 60 + duration.mins;
        const preChartMins =
          entry.pre_charting_Hours * 60 + entry.pre_charting_mins;
        totalDurationMins += durationMins;
        totalPreChartMins += preChartMins;
        projectsWithData.add(entry.project);

        if (!projectSubtotals[entry.project]) {
          projectSubtotals[entry.project] = {
            durationMins: 0,
            preChartMins: 0,
            count: 0,
          };
        }
        projectSubtotals[entry.project].durationMins += durationMins;
        projectSubtotals[entry.project].preChartMins += preChartMins;
        projectSubtotals[entry.project].count += 1;
      });

      const projectsWithNoData = projects
        .filter((p) => !projectsWithData.has(p.project_name))
        .map((p) => p.project_name)
        .sort((a, b) => a.localeCompare(b));

      setScribeSummary({
        totalDurationHours: Math.floor(totalDurationMins / 60),
        totalDurationMins: totalDurationMins % 60,
        totalPreChartHours: Math.floor(totalPreChartMins / 60),
        totalPreChartMins: totalPreChartMins % 60,
        projectSubtotals,
        projectsWithNoData,
      });
      setScribeData(filtered);
      toast.success(
        `Report generated: ${filtered.length} entries found, ${projectsWithNoData.length} projects with no data`
      );
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const handleDeleteScribe = async (entry) => {
    const label = `${entry.project} / ${entry.user} on ${new Date(entry.start_time).toLocaleString()}`;
    if (!window.confirm(`Delete this scribe entry?\n\n${label}`)) return;
    try {
      await axiosInstance.delete(`/scribe/${entry.scribeid}`);
      toast.success('Scribe entry deleted');
      await generateReport();
    } catch (error) {
      toast.error('Failed to delete scribe entry');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-dashboard-button"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="rounded-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-medium tracking-tight">Modify Scribe Data</h1>
              <p className="text-sm text-muted-foreground">Admin-only monthly view with delete capability</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="bg-white border border-border shadow-sm rounded-sm p-6">
          <h2 className="text-xl font-medium mb-6">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Month</Label>
              <Select
                value={String(scribeFilters.month)}
                onValueChange={(v) => setScribeFilters({ ...scribeFilters, month: parseInt(v) })}
              >
                <SelectTrigger data-testid="admin-month-select" className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m.value} value={String(m.value)}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Year</Label>
              <Select
                value={String(scribeFilters.year)}
                onValueChange={(v) => setScribeFilters({ ...scribeFilters, year: parseInt(v) })}
              >
                <SelectTrigger data-testid="admin-year-select" className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                data-testid="admin-generate-report-button"
                onClick={generateReport}
                className="w-full h-10 rounded-sm bg-primary hover:bg-primary/90"
              >
                Generate Report
              </Button>
            </div>
          </div>
        </div>

        {scribeSummary && (
          <div className="bg-white border border-border shadow-sm rounded-sm p-6">
            <h3 className="text-lg font-medium mb-4">Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="p-4 bg-muted/30 rounded-sm">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Total Entries</p>
                <p className="text-3xl font-semibold text-primary">{scribeData.length}</p>
              </div>
              <div className="p-4 bg-muted/30 rounded-sm">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Total Duration</p>
                <p className="text-3xl font-semibold text-primary">
                  {scribeSummary.totalDurationHours}h {scribeSummary.totalDurationMins}m
                </p>
              </div>
              <div className="p-4 bg-muted/30 rounded-sm">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Total Pre-charting</p>
                <p className="text-3xl font-semibold text-primary">
                  {scribeSummary.totalPreChartHours}h {scribeSummary.totalPreChartMins}m
                </p>
              </div>
            </div>
            <div className="p-4 bg-primary/5 rounded-sm border border-primary/20">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                Combined Total (Duration + Pre-charting)
              </p>
              <p className="text-3xl font-semibold text-primary">
                {(
                  (scribeSummary.totalDurationHours * 60 +
                    scribeSummary.totalDurationMins +
                    scribeSummary.totalPreChartHours * 60 +
                    scribeSummary.totalPreChartMins) /
                  60
                ).toFixed(2)}{' '}
                hours
              </p>
            </div>
          </div>
        )}

        <div className="bg-white border border-border shadow-sm rounded-sm p-6">
          <h3 className="text-lg font-medium mb-4">Report Results</h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Project</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">User</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Start Time</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">End Time</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Duration</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Pre-chart</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Auth</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-muted-foreground uppercase tracking-wider text-xs">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scribeData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No data. Select month/year and click Generate Report.
                    </TableCell>
                  </TableRow>
                ) : (
                  scribeData.map((entry, index) => {
                    const duration = calculateTimeDifference(entry.start_time, entry.end_time);
                    const exceedsTenHours = checkExceedsTenHours(entry);
                    const isLastInGroup =
                      index === scribeData.length - 1 ||
                      scribeData[index + 1].project !== entry.project;
                    const subtotal =
                      isLastInGroup && scribeSummary?.projectSubtotals?.[entry.project];

                    return (
                      <React.Fragment key={entry.scribeid}>
                        <TableRow
                          className={`border-b hover:bg-muted/50 ${
                            exceedsTenHours ? 'bg-yellow-50 border-l-4 border-l-yellow-500' : ''
                          }`}
                          data-testid="admin-scribe-row"
                        >
                          <TableCell className="px-2 py-3 text-sm">{entry.project}</TableCell>
                          <TableCell className="px-2 py-3 text-sm">{entry.user}</TableCell>
                          <TableCell className="px-2 py-3 text-sm">{new Date(entry.start_time).toLocaleString()}</TableCell>
                          <TableCell className="px-2 py-3 text-sm">{new Date(entry.end_time).toLocaleString()}</TableCell>
                          <TableCell className="px-2 py-3 text-sm font-medium">
                            {duration.hours}h {duration.mins}m
                            {exceedsTenHours && (
                              <span className="ml-2 text-xs bg-yellow-500 text-white px-2 py-0.5 rounded">&gt;10hrs</span>
                            )}
                          </TableCell>
                          <TableCell className="px-2 py-3 text-sm">
                            {entry.pre_charting_Hours}h {entry.pre_charting_mins}m
                          </TableCell>
                          <TableCell className="px-2 py-3 text-sm">{entry.prior_authorization ? 'Yes' : 'No'}</TableCell>
                          <TableCell className="px-2 py-3">
                            <Button
                              data-testid="admin-delete-scribe-button"
                              variant="ghost"
                              size="sm"
                              className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleDeleteScribe(entry)}
                              title="Delete this entry"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>

                        {subtotal && (
                          <TableRow className="bg-primary/5 font-medium border-b-2 border-primary/20">
                            <TableCell colSpan={3} className="px-2 py-2 text-sm">
                              <strong>Subtotal for {entry.project}</strong> ({subtotal.count} entries)
                            </TableCell>
                            <TableCell className="px-2 py-2 text-sm font-semibold text-primary">
                              Total: {((subtotal.durationMins + subtotal.preChartMins) / 60).toFixed(2)} hrs
                            </TableCell>
                            <TableCell className="px-2 py-2 text-sm font-semibold text-primary">
                              {Math.floor(subtotal.durationMins / 60)}h {subtotal.durationMins % 60}m
                            </TableCell>
                            <TableCell className="px-2 py-2 text-sm font-semibold text-primary">
                              {Math.floor(subtotal.preChartMins / 60)}h {subtotal.preChartMins % 60}m
                            </TableCell>
                            <TableCell colSpan={2}></TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {scribeSummary?.projectsWithNoData?.length > 0 && (
          <div className="bg-white border border-border shadow-sm rounded-sm p-6">
            <h3 className="text-lg font-medium mb-2">Projects with No Data</h3>
            <p className="text-xs text-muted-foreground mb-4">
              {scribeSummary.projectsWithNoData.length} project(s) had no entries in the selected month/year.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {scribeSummary.projectsWithNoData.map((p) => (
                <div key={p} className="text-sm py-1 px-2 bg-muted/30 rounded-sm">
                  {p}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMonthlyReport;
